/* eslint-disable no-unused-vars */
/* eslint-disable react/react-in-jsx-scope */
import {
  Box,
  Grid,
  Button,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Stack,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Tooltip,
} from "@mui/material";
import { tokens } from "../../theme";
import { mockTransactions } from "../../data/mockData";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EmailIcon from "@mui/icons-material/Email";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import TrafficIcon from "@mui/icons-material/Traffic";
import Header from "../../components/Header";
import LineChart from "../../components/LineChart";
import PieChart from "../../components/PieChart";
import GeographyChart from "../../components/GeographyChart";
import BarChart from "../../components/BarChart";
import StatBox from "../../components/StatBox";
import ProgressCircle from "../../components/ProgressCircle";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import GavelIcon from "@mui/icons-material/Gavel";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import Cases from "../CaseManagement/Case";
import { useState, useEffect } from "react";
import { graphqlQuery } from "../../data/Axios/DynamicService";
import {
  caseTaskForDashboard,
  mainDashboardQuery,
  recentCaseTasks,
} from "../../data/Axios/queries";
import { caseManagementUrl, companyManagementUrl } from "../../config";
import MainCard from "../../components/MainCard";
import OrderTable from "./OrdersTable";
import WorkIcon from "@mui/icons-material/Work";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import Link from "@mui/material/Link";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [data, setData] = useState([]);
  const [task, setTasks] = useState([]);

  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [lastAccessed, setLastAccessed] = useState(null);
  const [activeTime, setActiveTime] = useState("0m");
  useEffect(() => {
    let isMounted = true;
    const now = new Date();
    const previousAccess = localStorage.getItem("sheriaProDashboardLastAccessed");
    const sessionStartedAt = Number(sessionStorage.getItem("sheriaProDashboardSessionStartedAt")) || now.getTime();
    if (!sessionStorage.getItem("sheriaProDashboardSessionStartedAt")) {
      sessionStorage.setItem("sheriaProDashboardSessionStartedAt", String(sessionStartedAt));
    }
    setLastAccessed(previousAccess ? new Date(previousAccess) : null);
    localStorage.setItem("sheriaProDashboardLastAccessed", now.toISOString());

    const updateActiveTime = () => {
      const elapsedMinutes = Math.max(0, Math.floor((Date.now() - sessionStartedAt) / 60000));
      setActiveTime(elapsedMinutes >= 60 ? `${Math.floor(elapsedMinutes / 60)}h ${elapsedMinutes % 60}m` : `${elapsedMinutes}m`);
    };
    updateActiveTime();
    async function fetchAllData() {
      try {
        if (isMounted) {
          const Response = await graphqlQuery(
            mainDashboardQuery,
            companyManagementUrl.uri
          );
          if (Response) {
            setData(Response);
            setLastUpdated(new Date());
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchAllData();
    const refreshTimer = window.setInterval(fetchAllData, 60000);
    const activityTimer = window.setInterval(updateActiveTime, 60000);
    return () => {
      isMounted = false;
      window.clearInterval(refreshTimer);
      window.clearInterval(activityTimer);
    };
  }, []);
  const metricCardSx = {
    height: "100%",
    minHeight: 152,
    p: 2.5,
    borderRadius: "18px",
    border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,.13)" : "rgba(255,255,255,.72)"}`,
    bgcolor: theme.palette.mode === "dark" ? "rgba(43, 59, 88, .58)" : "rgba(255,255,255,.48)",
    backdropFilter: "blur(20px) saturate(150%)",
    WebkitBackdropFilter: "blur(20px) saturate(150%)",
    boxShadow: theme.palette.mode === "dark" ? "inset 0 1px rgba(255,255,255,.10), 0 18px 42px rgba(0,0,0,.24)" : "inset 0 1px rgba(255,255,255,.8), 0 18px 42px rgba(15, 23, 42, .10)",
    transition: "transform .2s ease, box-shadow .2s ease",
    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 18px 38px rgba(0,0,0,.24)" },
  };
  const glassPanelSx = {
    bgcolor: theme.palette.mode === "dark" ? "rgba(43, 59, 88, .52)" : "rgba(255,255,255,.48)",
    backdropFilter: "blur(22px) saturate(150%)",
    WebkitBackdropFilter: "blur(22px) saturate(150%)",
    border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,.12)" : "rgba(255,255,255,.75)"}`,
    boxShadow: theme.palette.mode === "dark" ? "inset 0 1px rgba(255,255,255,.08), 0 18px 42px rgba(0,0,0,.2)" : "inset 0 1px rgba(255,255,255,.85), 0 18px 42px rgba(15, 23, 42, .1)",
  };
  const sectionTitleSx = { fontWeight: 800, letterSpacing: "-.02em", color: colors.grey[100] };
  const executiveItems = [
    ["Cases Closed", data.casesClosed], ["Court Cases", data.numberOfCourtCases],
    ["Case Events", data.numberOfCaseEvents], ["New Clients", data.numberOfNewClients],
    ["Matter Complete", data.matterCompletion], ["Tasks Done", data.taskCompletion],
  ];
  return (
    <Box sx={{ minHeight: "100%", position: "relative", overflow: "hidden", background: theme.palette.mode === "dark" ? "radial-gradient(circle at 8% 0%, rgba(73, 192, 187, .15), transparent 31%), radial-gradient(circle at 92% 18%, rgba(102, 112, 250, .16), transparent 27%), #111a2d" : "radial-gradient(circle at 8% 0%, rgba(92, 206, 198, .25), transparent 31%), radial-gradient(circle at 92% 18%, rgba(129, 154, 255, .22), transparent 27%), #eaf2f6" }}>
      <Grid container rowSpacing={2.5} columnSpacing={2.75} padding={{ xs: 2, md: 3 }}>
        {/* row 1 */}
        <Grid item xs={12} sx={{ mb: -2.25 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
            <Box>
              <Typography variant="h2" fontWeight={800} sx={{ background: "linear-gradient(90deg, #18c9f5, #a78bfa)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", letterSpacing: "-.04em" }}>Sheria Pro</Typography>
              <Typography variant="body1" sx={{ color: colors.grey[300], mt: .5 }}>Welcome back — manage your legal cases seamlessly.</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mt={1.5}>
                <Chip icon={<AccessTimeRoundedIcon />} label={lastAccessed ? `Last accessed ${lastAccessed.toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}` : "First dashboard visit"} size="small" sx={{ bgcolor: "rgba(255,255,255,.07)", color: colors.grey[200] }} />
                <Chip icon={<BoltRoundedIcon />} label={`Active for ${activeTime}`} size="small" sx={{ bgcolor: "rgba(24,201,245,.12)", color: "#28d9fa" }} />
              </Stack>
            </Box>
            <Tooltip title="Dashboard data refreshes every minute">
              <Chip icon={<RefreshRoundedIcon />} label={lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "Updating…"} variant="outlined" sx={{ color: colors.greenAccent[400], borderColor: colors.greenAccent[700], fontWeight: 600 }} />
            </Tooltip>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Link
            href="/Cases"
            style={{ textDecoration: "none", color: "inherit" }}
            // onclick={() => navigate("/Cases")}
          >
            <Box
              gridColumn="span 3"
              sx={{ ...metricCardSx, borderTop: "2px solid #14c8f5" }}
            >
              <StatBox
                title={data.numberOfCases}
                subtitle="Cases"
                progress="0.75"
                increase={`+${data.courtCasePercentage || 0}%`}
                icon={
                  <WorkHistoryIcon
                    sx={{ color: colors.greenAccent[400], fontSize: "26px" }}
                  />
                }
              />
            </Box>
          </Link>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Link
            href="/corporate-clients"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <Box
              gridColumn="span 3"
              sx={{ ...metricCardSx, borderTop: "2px solid #f72585" }}
            >
              <StatBox
                title={data.numberOfCorporateClients}
                subtitle="Corporate Clients"
                progress="0.50"
                increase={`+${data.corporateClientPercentage || 0}%`}
                icon={
                  <GroupAddIcon
                    sx={{ color: colors.greenAccent[400], fontSize: "26px" }}
                  />
                }
                sx={{
                  "& .MuiTypography-root": {
                    fontSize: isSmallScreen ? "0.75rem" : "1rem",
                  },
                }}
              />
            </Box>
          </Link>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Link
            href="/users"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <Box
              gridColumn="span 3"
              sx={{ ...metricCardSx, borderTop: "2px solid #c77dff" }}
            >
              <StatBox
                title={data.team}
                subtitle="Team"
                progress="0.30"
                increase={`+${data.courtCasePercentage || 0}%`}
                icon={
                  <GroupAddIcon
                    sx={{ color: colors.greenAccent[400], fontSize: "26px" }}
                  />
                }
                sx={{
                  "& .MuiTypography-root": {
                    fontSize: isSmallScreen ? "0.75rem" : "1rem",
                  },
                }}
              />
            </Box>
          </Link>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Link
            href="/individual-clients"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <Box
              gridColumn="span 3"
              sx={{ ...metricCardSx, borderTop: "2px solid #ffb000" }}
            >
              <StatBox
                title={data.numberOfIndividualClients}
                subtitle="Individual Clients"
                progress="0.80"
                increase={`+${data.individualClientPercentage || 0}%`}
                icon={
                  <GroupAddIcon
                    sx={{ color: colors.greenAccent[400], fontSize: "26px" }}
                  />
                }
                sx={{
                  "& .MuiTypography-root": {
                    fontSize: isSmallScreen ? "0.75rem" : "1rem",
                  },
                }}
              />
            </Box>
          </Link>
        </Grid>
        <Grid
          item
          md={8}
          sx={{ display: { sm: "none", md: "block", lg: "none" } }}
        />
        {/* row 2 */}
        <Grid item xs={12} md={7} lg={8}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h5" sx={sectionTitleSx}>Case Overview</Typography>
            </Grid>
            <Grid item />
          </Grid>
          <Box
            sx={{ mt: 1, ...glassPanelSx }}
            height="400px"
            borderRadius="18px"
          >
            <BarChart />
            {/* <LineChart /> */}
          </Box>
        </Grid>
        <Grid item xs={12} md={5} lg={4}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h5" sx={sectionTitleSx}>Executive Summary</Typography>
            </Grid>
          </Grid>
          <MainCard sx={{ mt: 1, height: "400px", borderRadius: "18px", ...glassPanelSx }} content={false}>
            <Box sx={{ p: 3, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.5 }}>
              {executiveItems.map(([label, value]) => (
                <Box key={label} sx={{ minHeight: 84, borderRadius: "12px", border: "1px solid rgba(24, 201, 245, .32)", bgcolor: "rgba(24, 201, 245, .07)", display: "grid", placeItems: "center", textAlign: "center", p: 1 }}>
                  <Typography variant="h3" fontWeight={800} sx={{ color: "#18c9f5" }}>{value ?? "—"}</Typography>
                  <Typography variant="caption" sx={{ color: colors.grey[200] }}>{label}</Typography>
                </Box>
              ))}
            </Box>
          </MainCard>
        </Grid>

        {/* row 3 */}
        <Grid item xs={12} md={6} lg={6} height="400px">
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h5" sx={sectionTitleSx}>Recent Tasks</Typography>
            </Grid>
            <Grid item />
          </Grid>
          <MainCard sx={{ mt: 2, borderRadius: "18px", ...glassPanelSx }} content={false}>
            <OrderTable />
          </MainCard>
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Grid item>
              <Typography variant="h5" sx={sectionTitleSx}>Document Storage</Typography>
            </Grid>
            <Grid item />
          </Grid>
          {/* <Grid container rowSpacing={2.5} columnSpacing={2.75}>
            <Grid item xs={4} sm={4} md={4} lg={4}>
              <Link
                href="/Cases"
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Box
                  gridColumn="span 3"
                  backgroundColor={colors.primary[400]}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="0.5rem"
                  padding="15px"
                  boxShadow="0px 4px 10px rgba(0, 0, 0, 0.1)"
                >
                  <StatBox
                    title={data.numberOfCases}
                    subtitle="Won"
                    progress="0.75"
                    increase={`+${data.courtCasePercentage || 0}%`}
                    icon={
                      <WorkHistoryIcon
                        sx={{
                          color: colors.greenAccent[400],
                          fontSize: "26px",
                        }}
                      />
                    }
                  />
                </Box>
              </Link>
            </Grid>
            <Grid item xs={4} sm={4} md={4} lg={4}>
              <Link
                href="/Cases"
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Box
                  gridColumn="span 3"
                  backgroundColor={colors.primary[400]}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="0.5rem"
                  padding="15px"
                  boxShadow="0px 4px 10px rgba(0, 0, 0, 0.1)"
                >
                  <StatBox
                    title={data.numberOfCases}
                    subtitle="Lost"
                    progress="0.75"
                    increase={`+${data.courtCasePercentage || 0}%`}
                    icon={
                      <WorkHistoryIcon
                        sx={{
                          color: colors.greenAccent[400],
                          fontSize: "26px",
                        }}
                      />
                    }
                  />
                </Box>
              </Link>
            </Grid>
            <Grid item xs={4} sm={4} md={4} lg={4}>
              <Link
                href="/Cases"
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Box
                  gridColumn="span 3"
                  backgroundColor={colors.primary[400]}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="0.5rem"
                  padding="15px"
                  boxShadow="0px 4px 10px rgba(0, 0, 0, 0.1)"
                >
                  <StatBox
                    title={data.numberOfCases}
                    subtitle="Dropped"
                    progress="0.75"
                    increase={`+${data.courtCasePercentage || 0}%`}
                    icon={
                      <WorkHistoryIcon
                        sx={{
                          color: colors.greenAccent[400],
                          fontSize: "26px",
                        }}
                      />
                    }
                  />
                </Box>
              </Link>
            </Grid>
          </Grid> */}
          <MainCard sx={{ mt: 2, borderRadius: "18px", ...glassPanelSx }} content={false}>
            <Box height={"400px"}>
              <PieChart />
              {/* <LineChart /> */}
            </Box>
          </MainCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
